import { useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	LIST_VIEW_SOURCE,
	BUSINESS_TIMELINE_SOURCE,
	UNKNOWN_SOURCE,
	type ConfigureSource,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

interface AnalyticsContextEntry {
	source?: string;
	attributes?: Record<string, unknown>;
}

const extractSourceFromContext = (
	analyticsEvent: UIAnalyticsEvent | null | undefined,
	sourceName: string,
): string | undefined => {
	if (!analyticsEvent?.context || !Array.isArray(analyticsEvent.context)) {
		return undefined;
	}

	const contextEntry = analyticsEvent.context.find(
		(entry: AnalyticsContextEntry) => entry?.source === sourceName,
	);

	return contextEntry?.source;
};

export const useExtractSourceFromNamespace = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = useMemo(() => createAnalyticsEvent({}), [createAnalyticsEvent]);

	const issueNavigatorListViewScreen = useMemo(
		() => extractSourceFromContext(analyticsEvent, 'issueNavigatorListViewScreen'),
		[analyticsEvent],
	);

	const jiraTimelineScreen = useMemo(
		() => extractSourceFromContext(analyticsEvent, 'jiraTimelineScreen'),
		[analyticsEvent],
	);

	const businessProjectContextScreen = useMemo(
		() => extractSourceFromContext(analyticsEvent, 'businessProjectContextScreen'),
		[analyticsEvent],
	);

	const mappedSource = useMemo((): ConfigureSource => {
		if (issueNavigatorListViewScreen) {
			return LIST_VIEW_SOURCE;
		}
		if (businessProjectContextScreen && jiraTimelineScreen) {
			return BUSINESS_TIMELINE_SOURCE;
		}
		return UNKNOWN_SOURCE;
	}, [issueNavigatorListViewScreen, businessProjectContextScreen, jiraTimelineScreen]);

	return mappedSource;
};
